<script lang="ts" setup>
import AuthorityBanner from '../AuthorityBanner/AuthorityBanner.vue';
import DiagnoseLink from '../DiagnoseLink.vue';
import EveryonesReviews from '../EveryonesReviews/EveryonesReviews.vue';
import FAQ, { type QAndA } from '../FAQ/FAQ.vue';
import HeroStartTitle from '../HeroStartTitle.vue';
import LpBreadcrumbs, { type Breadcrumbs } from '../LpBreadcrumbs.vue';
import MediaSection from '../MediaSection.vue';
import ProblemPresentation from '../ProblemPresentation/ProblemPresentation.vue';
import ProductLpHero from '../ProductLpHero.vue';
import ProductLpTemplateConsultProfessional from '../ProductLpTemplateConsultProfessional.vue';
import ScrollToTopButton from '../ScrollToTopButton/ScrollToTopButton.vue';
import SeminarSection from '../SeminarSection.vue';
import Steps from '../Steps/Steps.vue';
import WhereToConsult from '../WhereToConsult/WhereToConsult.vue';
import WhyFreeConsultation from '../WhyFreeConsultation/WhyFreeConsultation.vue';
import YouCanDoThis from '../YouCanDoThis/YouCanDoThis.vue';
import { commonFaqList } from '../common/commonFaqList';
import type { KvImageSrcset } from '../kvImageSrcsets';
import okanekoMagazineBanner from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/okaneko-magazine-banner.png/okaneko-magazine-banner.webp';
import {
  SeminarFragment,
  useProductLpDevKeywordsQuery,
} from '../useProductLpDevKeywordsQuery';
import ProblemPresentationCard1 from './ProblemPresentationCard1.vue';
import ProblemPresentationCard2 from './ProblemPresentationCard2.vue';
import ProblemPresentationCard3 from './ProblemPresentationCard3.vue';
import ProblemPresentationHeading from './ProblemPresentationHeading.vue';
import WhereToConsultCard1 from './WhereToConsultCard/WhereToConsultCard1.vue';
import WhereToConsultCard2 from './WhereToConsultCard/WhereToConsultCard2.vue';
import WhereToConsultCard3 from './WhereToConsultCard/WhereToConsultCard3.vue';
import WhereToConsultCard4 from './WhereToConsultCard/WhereToConsultCard4.vue';
import WhereToConsultCard5 from './WhereToConsultCard/WhereToConsultCard5.vue';
import YouCanDoThisCard1 from './YouCanDoThisCard1.vue';
import YouCanDoThisCard2 from './YouCanDoThisCard2.vue';
import YouCanDoThisCard3 from './YouCanDoThisCard3.vue';
import kvImage380 from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/ideco/kv_ideco.png/kv_ideco_width=380.webp';
import kvImage560 from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/ideco/kv_ideco.png/kv_ideco_width=560.webp';
import kvImage600 from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/ideco/kv_ideco.png/kv_ideco_width=600.webp';
import kvImage1200 from '/home/runner/work/shadow/shadow/packages/app/node_modules/.cache/home/runner/work/shadow/shadow/packages/app/src/components/templates/ProductLpTemplate/ideco/kv_ideco.png/kv_ideco_width=1200.webp';
import PointsSummary from '@/components/molecules/PointsSummary/PointsSummary.vue';
import AppFooter from '@/components/organisms/AppFooter/AppFooter.vue';
import InterviewCard from '~/components/organisms/InterviewCard/InterviewCard.vue';

const keyword = 'iDeCo';

const { data } = await useProductLpDevKeywordsQuery({
  keywords: [keyword],
});
const seminars = useComputedFragment(
  SeminarFragment,
  () => data.value?.filterSeminars.edges.map((elm) => elm.node) ?? []
);

const title =
  'iDeCoの始め方を無料相談｜iDeCoのやり方をFPに相談するならオカネコ';
const description =
  'オカネコでは、iDeCoの始め方を無料で相談できます。iDeCoを始めたいけど、何から始めればいいかわからない方へ。FP資格を持った専門家が初めての一歩をサポートします。';

const dateModified = '2024-09-24';

const breadcrumbs: Breadcrumbs = [
  {
    label: 'オカネコ',
    location: '/',
    description: useConstants().WEB.description,
  },
  {
    label: `${keyword}のやり方・相談`,
    description: description,
  },
];

const kvImageSrcset: KvImageSrcset = {
  1200: kvImage1200,
  600: kvImage600,
  560: kvImage560,
  380: kvImage380,
};

const faqList: QAndA[] = [
  {
    q: 'iDeCoについてよくわからないのですが、サポートしてもらえますか？',
    a: 'はい、オカネコのアドバイザーがiDeCoの基本から運用方法まで丁寧に説明し、お客様に最適なプランを提案します。',
  },
  {
    q: 'iDeCoは今から始めても遅くないですか？',
    a: 'iDeCoは早く始めるほど長期的に利益を得られる可能性が高いといえます。オンライン面談では、お客様のライフプランや家計の状況に合わせて、最適なタイミングやプランを提案します。',
  },
  {
    q: '相談には、どのくらい時間がかかりますか？',
    a: '約30分～1時間程度です。基本的にはお客様のご都合やペースに合わせて進めさせていただきます。事前に具体的なご質問や相談内容を用意していただくと、よりスムーズに進行できます。',
  },
  {
    q: '地方に住んでいても相談できる？',
    a: 'はい、地方にお住まいの方でもご相談いただけます。オカネコは、すべてオンラインで相談いただけますので場所を問わず全国どこからでもご利用いただけます。',
  },
  ...commonFaqList,
];

const mediaProps = {
  location: 'https://okane-kenko.jp/media/',
  imageSrc: okanekoMagazineBanner,
  relatedArticleLinks: [
    {
      title: 'iDeCoを始めるならどこがいい？金融機関おすすめ情報【2024】',
      location: 'https://okane-kenko.jp/media/category/ideco/',
    },
    {
      title:
        'iDeCoはデメリットしかないからやらないほうがいいってホント？【だまされるな】',
      location: 'https://okane-kenko.jp/media/ideco-demerit/',
    },
  ],
};

const interviewIds = ['1', '2', '3', '4'] as const;

useAppMeta({
  title: title,
  hasTitleSuffix: false,
  description: description,
});

const {
  public: { appOrigin },
} = useRuntimeConfig();
const route = useRoute();
const currentCanonicalUrl = appOrigin + route.path.replace(/\/+$/, '');
useJsonldWebPageForProductLP(
  currentCanonicalUrl,
  title,
  description,
  dateModified
);
</script>

<template>
  <div :class="$style.layout">
    <LpBreadcrumbs :breadcrumbs="breadcrumbs" />
    <ProductLpHero :class="$style.hero" :kv-image-srcset="kvImageSrcset">
      <template #heading> {{ keyword }}についてお悩みの方へ</template>
      <template #title>
        <HeroStartTitle :keyword="keyword" />
      </template>
      <template #description>
        iDeCoを始めたいけど、何から始めればいいかわからない方へ。FP資格を持った専門家が初めての一歩をサポートします。
      </template>
    </ProductLpHero>
    <AuthorityBanner :class="$style.authority" />
    <ProblemPresentation :class="$style.problemPresentation" :keyword="keyword">
      <template #heading>
        <ProblemPresentationHeading />
      </template>
      <template #default>
        <ProblemPresentationCard1 :image-number="1" />
        <ProblemPresentationCard2 :image-number="2" />
        <ProblemPresentationCard3 :image-number="3" />
      </template>
    </ProblemPresentation>
    <WhereToConsult
      :class="$style.whereToConsult"
      :keyword="keyword"
      :title="`${keyword}はどこに相談すべき？おすすめの相談先は？`"
      :date-modified="dateModified"
    >
      <WhereToConsultCard1 :image-number="0" />
      <WhereToConsultCard2 :image-number="1" />
      <WhereToConsultCard3 :image-number="2" />
      <WhereToConsultCard4 :image-number="3" />
      <WhereToConsultCard5 :image-number="4" />
    </WhereToConsult>
    <ProductLpTemplateConsultProfessional
      :keyword="keyword"
      :class="$style.consultProfessional"
    />
    <YouCanDoThis :keyword="keyword" :class="$style.youCanDoThis">
      <YouCanDoThisCard1 :image-number="1" />
      <YouCanDoThisCard2 :image-number="2" />
      <YouCanDoThisCard3 :image-number="3" />
    </YouCanDoThis>
    <WhyFreeConsultation :class="$style.whyFreeConsultation" />
    <PointsSummary :class="$style.pointsSummary" />
    <DiagnoseLink :class="$style.diagnoseLink1" />
    <Steps :class="$style.steps" />
    <EveryonesReviews :class="$style.everyonesReviews">
      <InterviewCard
        v-for="id in interviewIds"
        :id="id"
        :key="id"
        variant="complicated"
      />
    </EveryonesReviews>
    <FAQ :class="$style.faq" :faq-list="faqList" />
    <DiagnoseLink :class="$style.diagnoseLink2" />
    <section
      v-if="seminars.length > 0 || !!mediaProps"
      :class="$style.seminarAndMedia"
    >
      <SeminarSection
        :seminars="seminars"
        seminars-link="/seminar?categories=ASSET_MANAGEMENT"
        :category="keyword"
      />
      <MediaSection :keyword="keyword" :media="mediaProps" />
      <DiagnoseLink :class="$style.diagnoseLink3" />
    </section>
    <AppFooter />
    <ScrollToTopButton :class="$style.layout__scrollToTop"></ScrollToTopButton>
  </div>
</template>

<style lang="scss" module>
img {
  vertical-align: top;
}

.layout {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  @include mobile-only {
    padding-bottom: 200px;
  }
  &__scrollToTop {
    display: block;
    position: absolute;
    right: 40px;
    bottom: 40px;
    @include mobile-only {
      right: 0;
      left: 0;
      margin: auto auto;
      bottom: 64px;
    }
  }
}

.hero {
  align-self: stretch;
}

.authority {
  max-width: 520px;
  width: 100vw;
}

.problemPresentation {
  margin-top: 40px;
}

.whereToConsult {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.researchData {
  margin-top: 64px;
  width: 100%;

  @include mobile-only {
    margin-top: 40px;
  }
}

.consultProfessional {
  margin-top: 80px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.youCanDoThis {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.whyFreeConsultation {
  margin-top: 40px;

  @include mobile-only {
    margin-top: 20px;
  }
}

@import '../_constants.scss';
.pointsSummary {
  max-width: $content-width;

  margin-top: 40px;
  padding: 0 16px;

  @include mobile-only {
    margin-top: 20px;
  }
}

.seminarAndMedia {
  width: 100%;
  margin-top: 64px;
  padding: 56px 0;
  background-color: $color-surface-accent-primary-light;
  @include mobile-only {
    padding: 32px 0;
  }
  & > * {
    margin-right: auto;
    margin-left: auto;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.steps {
  margin-top: 64px;

  @include mobile-only {
    margin-top: 48px;
  }
}

.faq {
  width: 100%;
}

.diagnoseLink1 {
  margin-top: 40px;
}
.diagnoseLink2 {
  margin-top: 40px;
}
.diagnoseLink3 {
  margin-top: 56px;
}
</style>
